import { memo, useCallback } from 'react';

import { WorkspaceAgentThumbnailCard } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentThumbnailCard';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';

export type WorkspaceAgentThumbnailRowItemProps = {
  'data-testid'?: string;
  onOpen?: (id: string) => void;
  thumbnail: WorkspaceAgentThumbnail;
};

/**
 * One cell of the thumbnail row. Exists so the row's `onOpen` can be bound to a thumbnail id once per
 * card rather than rebuilt on every render of the row.
 */
export const WorkspaceAgentThumbnailRowItem = memo(
  ({ 'data-testid': testId, onOpen, thumbnail }: WorkspaceAgentThumbnailRowItemProps) => {
    const handleOpen = useCallback(() => onOpen?.(thumbnail.id), [onOpen, thumbnail.id]);
    const isOpenable = Boolean(onOpen) && thumbnail.canOpen !== false && !thumbnail.isUnavailable;

    return (
      // Paired with the card's height to hold a 3:2 frame — see `WorkspaceAgentThumbnailCard`.
      <div className="w-[108px] shrink-0 p-1" data-testid={testId}>
        <WorkspaceAgentThumbnailCard
          data-thumbnail-id={thumbnail.id}
          isUnavailable={thumbnail.isUnavailable}
          label={thumbnail.label}
          onOpen={isOpenable ? handleOpen : undefined}
          thumbnailUrl={thumbnail.thumbnailUrl}
        />
      </div>
    );
  },
);

WorkspaceAgentThumbnailRowItem.displayName = 'WorkspaceAgentThumbnailRowItem';
